
import React from "react";
import CompanyLayout from "@/components/CompanyLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Briefcase, Coffee, UserPlus, Heart, Award, Sparkles, Building2 } from "lucide-react";

const Careers = () => {
  const pageTitle = "Careers at Assembly";
  const pageDescription = "Join our team and help transform workplace culture through employee recognition.";

  const openRoles = [
    {
      title: "Senior Software Engineer",
      department: "Engineering",
      location: "San Francisco, CA or Remote",
      type: "Full-time"
    },
    {
      title: "Product Designer",
      department: "Design",
      location: "San Francisco, CA or Remote",
      type: "Full-time"
    },
    {
      title: "Customer Success Manager",
      department: "Customer Success",
      location: "Remote",
      type: "Full-time"
    },
    {
      title: "Sales Development Representative",
      department: "Sales",
      location: "San Francisco, CA",
      type: "Full-time"
    },
    {
      title: "Content Marketing Specialist",
      department: "Marketing",
      location: "Remote",
      type: "Full-time"
    },
    {
      title: "DevOps Engineer",
      department: "Engineering",
      location: "San Francisco, CA or Remote",
      type: "Full-time"
    }
  ];

  const perks = [
    {
      icon: <Coffee className="h-8 w-8 text-assembly-blue" />,
      title: "Flexible Work",
      description: "Remote-first with flexible hours. Work when and where you work best."
    },
    {
      icon: <Heart className="h-8 w-8 text-assembly-pink" />,
      title: "Health & Wellness",
      description: "Comprehensive health benefits and wellness stipend for your physical and mental wellbeing."
    },
    {
      icon: <Award className="h-8 w-8 text-assembly-orange" />,
      title: "Competitive Compensation",
      description: "Generous base pay, equity, and performance bonuses to reward your contributions."
    },
    {
      icon: <Sparkles className="h-8 w-8 text-assembly-purple" />,
      title: "Growth Opportunities",
      description: "Professional development budget and clear advancement paths to help you grow."
    }
  ];

  return (
    <CompanyLayout title={pageTitle} description={pageDescription}>
      {/* Hero Section */}
      <section className="bg-hero-gradient py-16 md:py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">Life @ Assembly</h1>
            <p className="text-xl md:text-2xl text-gray-700 mb-8">
              We are a people-first organization. Assembly was founded in 2017 on the belief that people can solve their unique problems by creating their own unique software. Come help us create the future of work and software!
            </p>
            <Button 
              className="bg-assembly-orange hover:bg-orange-600 text-white py-6 px-10 rounded-xl text-lg font-medium transition-all hover:shadow-lg flex items-center gap-2 mx-auto"
              onClick={() => {
                const openPositions = document.getElementById('open-positions');
                if (openPositions) openPositions.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              See Open Roles
              <ArrowRight className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Life @ Assembly Photos */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img 
                src="https://cdn.prod.website-files.com/6058ba87eec5717595807524/617c388fd2e7c2a44710c855_we-are-funny.avif" 
                alt="Team Assembly during morning standup" 
                className="w-full h-64 object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img 
                src="https://cdn.prod.website-files.com/6058ba87eec5717595807524/617c389ee59ac4506c6ecc1b_we-are-fun.webp" 
                alt="Team offsite" 
                className="w-full h-64 object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img 
                src="https://cdn.prod.website-files.com/6058ba87eec5717595807524/606deeb316ced73635fa7718_fist-bumping-in-conference-room-meeting.webp" 
                alt="Fist bumping in a conference room meeting" 
                className="w-full h-64 object-cover"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Our Numbers</h2>
              <p className="text-lg text-gray-700 mb-8">
                Our customer base is growing and we're adding new Assemblers! Assembly is backed and supported by some of the best advisors/VCs with a focus on the future of work.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-4xl font-bold text-assembly-orange mb-2">3,200+</div>
                  <div className="text-gray-600">Customers</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-assembly-blue mb-2">3+</div>
                  <div className="text-gray-600">Years in Business</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-assembly-purple mb-2">40+</div>
                  <div className="text-gray-600">Assemblers</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-assembly-green mb-2">$15M+</div>
                  <div className="text-gray-600">Fundraised</div>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Our Investors</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                  <div className="w-12 h-12 bg-assembly-blue rounded-lg flex items-center justify-center">
                    <Building2 className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">Homebrew</div>
                    <div className="text-gray-600">Hunter Walk</div>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                  <div className="w-12 h-12 bg-assembly-purple rounded-lg flex items-center justify-center">
                    <Sparkles className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">Wonder Ventures</div>
                    <div className="text-gray-600">Dustin Rosen</div>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                  <div className="w-12 h-12 bg-assembly-orange rounded-lg flex items-center justify-center">
                    <Award className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">Alpha Edison</div>
                    <div className="text-gray-600">Nate Redmond</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Perks & Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Perks & Benefits</h2>
            <p className="text-lg text-gray-700">
              We take care of our team so they can focus on doing their best work.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {perks.map((perk, index) => (
              <Card key={index} className="border-none shadow-lg hover-card-effect">
                <CardContent className="p-8">
                  <div className="mb-4">
                    {perk.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{perk.title}</h3>
                  <p className="text-gray-700">{perk.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section id="open-positions" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Open Positions</h2>
            <p className="text-lg text-gray-700 mb-12">
              We don't have any open positions at the moment, but we're always looking for exceptional talent.
            </p>
            
            <div className="bg-gray-50 rounded-2xl p-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Interested in joining Assembly?</h3>
              <p className="text-lg text-gray-700 mb-6">
                Even when we don't have specific openings, we'd love to hear from passionate individuals who want to help us build the future of employee recognition.
              </p>
              <p className="text-lg text-gray-700">
                Send us your resume and tell us about yourself at{" "}
                <a href="mailto:hr@joinassembly.com" className="text-assembly-blue hover:underline font-semibold">
                  hr@joinassembly.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Employee Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Assemblers Say...</h2>
            <p className="text-lg text-gray-700">See what our team thinks about working at Assembly</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Brooke Rosenthal */}
            <Card className="border-none shadow-lg hover-card-effect">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-6">
                  <img 
                    src="https://cdn.prod.website-files.com/6058ba87eec5717595807524/617b0a06717ad66c309c7bf8_brooke.avif" 
                    alt="Brooke Rosenthal" 
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <h3 className="font-bold text-gray-900">Brooke Rosenthal</h3>
                    <p className="text-gray-600">Product Designer</p>
                  </div>
                </div>
                <svg className="h-8 w-8 text-gray-200 mb-4" fill="currentColor" viewBox="0 0 32 32" aria-hidden="true">
                  <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                </svg>
                <p className="text-gray-700 mb-4">
                  "We all really care about our mission, and we use our own products religiously. I really appreciate the open dialogue between departments and with leadership on our progress, thoughts, and constructive feedback."
                </p>
                <p className="text-sm text-gray-500">"I'm a huge women's soccer fan! I follow the US Women's National Team religiously."</p>
              </CardContent>
            </Card>

            {/* Nikhil Sharma */}
            <Card className="border-none shadow-lg hover-card-effect">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-6">
                  <img 
                    src="https://cdn.prod.website-files.com/6058ba87eec5717595807524/617b09f4d8fd137d387e432e_Nikhil.avif" 
                    alt="Nikhil Sharma" 
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <h3 className="font-bold text-gray-900">Nikhil Sharma</h3>
                    <p className="text-gray-600">Technical Program Manager</p>
                  </div>
                </div>
                <svg className="h-8 w-8 text-gray-200 mb-4" fill="currentColor" viewBox="0 0 32 32" aria-hidden="true">
                  <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                </svg>
                <p className="text-gray-700 mb-4">
                  "I loved being a user of the product that I build. I get to use it before the release, share my requests as a user on how we can improve the user experience. I get to work with smart and motivated individuals who are committed to the mission."
                </p>
                <p className="text-sm text-gray-500">"I love playing table tennis. I wish to set up a pizzeria someday!"</p>
              </CardContent>
            </Card>

            {/* Katya Hill */}
            <Card className="border-none shadow-lg hover-card-effect">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-6">
                  <img 
                    src="https://cdn.prod.website-files.com/6058ba87eec5717595807524/617b0e48ed12da8dfe421bb9_Katya%20H.avif" 
                    alt="Katya Hill" 
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <h3 className="font-bold text-gray-900">Katya Hill</h3>
                    <p className="text-gray-600">Director of Growth</p>
                  </div>
                </div>
                <svg className="h-8 w-8 text-gray-200 mb-4" fill="currentColor" viewBox="0 0 32 32" aria-hidden="true">
                  <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                </svg>
                <p className="text-gray-700 mb-4">
                  "The camaraderie within the team is inspiring and heartwarming. It is an opportunity to grow and experience a startup life without the 'work-hard-play-hard' mentality."
                </p>
                <p className="text-sm text-gray-500">"I am a very competitive person, especially in sports"</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

    </CompanyLayout>
  );
};

export default Careers;
